import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, Plus, Shield, Trash2, Globe, Copy } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useQuery as useConvexQuery, useMutation as useConvexMutation } from "convex/react";
import { api } from "../../api";

interface DeviceBrowserWhitelistContentProps {
  deviceId: string;
}

export const DeviceBrowserWhitelistContent = ({ deviceId }: DeviceBrowserWhitelistContentProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const { toast } = useToast();

  // Fetch device data to get the deviceConfig ID
  const deviceData = useConvexQuery(api.devices.get.details, { device: deviceId as any });
  
  // Get device browser configuration including whitelisted sites using policies API
  const deviceBrowserConfig = useConvexQuery(
    api.devices.policies.get,
    deviceData ? { deviceConfig: deviceData.device.deviceConfig } : "skip"
  );

  // DEBUG: Log the exact API response for comparison with blacklist
  console.log('=== WHITELIST API RESPONSE DEBUG ===');
  console.log('Raw api.devices.policies.get response:', JSON.stringify(deviceBrowserConfig, null, 2));
  console.log('Response type:', typeof deviceBrowserConfig);
  console.log('Response keys:', deviceBrowserConfig ? Object.keys(deviceBrowserConfig) : 'null/undefined');
  console.log('=== END WHITELIST API RESPONSE DEBUG ===');

  // Convex mutation for updating device browser settings using policies API
  const updateDeviceBrowser = useConvexMutation(api.devices.policies.update);

  // Get current whitelisted sites - all URLs in allowlist are allowed
  const currentWhitelistedSites = deviceBrowserConfig ? 
    Object.keys(deviceBrowserConfig.allowlist || {}) : [];

  // DEBUG: Let's see the actual structure vs what API docs say
  console.log('=== WHITELIST DATA STRUCTURE DEBUG ===');
  console.log('deviceBrowserConfig?.allowlist:', deviceBrowserConfig?.allowlist);
  console.log('Type of allowlist:', typeof deviceBrowserConfig?.allowlist);
  console.log('Is allowlist an array?', Array.isArray(deviceBrowserConfig?.allowlist));
  console.log('Object.keys result:', Object.keys(deviceBrowserConfig?.allowlist || {}));
  console.log('currentWhitelistedSites:', currentWhitelistedSites);
  console.log('=== END WHITELIST DEBUG ===');

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copied",
        description: "Error message copied to clipboard",
      });
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleAddUrl = async () => {
    if (!newUrl.trim()) return;
    
    if (!deviceData?.device.deviceConfig) {
      toast({
        title: "Error",
        description: "Device configuration not found. Please try again.",
        variant: "destructive",
      });
      return;
    }

    // Check if URL is already in blacklist (true = blocked)
    const currentBlacklistedUrls = Object.keys(deviceBrowserConfig?.blocklist || {})
      .filter(url => (deviceBrowserConfig?.blocklist[url] as any) === true);
    
    if (currentBlacklistedUrls.includes(newUrl)) {
      toast({
        title: "Cannot Add to Whitelist",
        description: `${newUrl} is currently in the blacklist. Please remove it from the blacklist first.`,
        variant: "destructive",
      });
      return;
    }

    try {
      // Get current whitelisted URLs and add the new one
      const newWhitelistedUrls = [...currentWhitelistedSites, newUrl];
      
      if (updateDeviceBrowser) {
        await updateDeviceBrowser({
          device: deviceId as any,
          allowedUrls: newWhitelistedUrls
        });
      }
      
      toast({
        title: "URL Added",
        description: `${newUrl} has been added to the whitelist.`,
      });
      setNewUrl("");
    } catch (error) {
      console.error('Error adding URL to whitelist:', error);
      
      const errorMessage = `Failed to add URL to whitelist: ${error?.message || 'Unknown error'}`;
      
      toast({
        title: "Error",
        description: (
          <div className="flex items-center justify-between gap-2">
            <span className="flex-1">{errorMessage}</span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => copyToClipboard(errorMessage)}
              className="h-6 w-6 p-0"
            >
              <Copy className="h-3 w-3" />
            </Button>
          </div>
        ),
        variant: "destructive",
      });
    }
  };

  const handleRemoveUrl = async (url: string) => {
    if (!deviceData?.device.deviceConfig) {
      toast({
        title: "Error",
        description: "Device configuration not found. Please try again.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Remove the URL from whitelisted sites
      const newWhitelistedUrls = currentWhitelistedSites.filter(siteUrl => siteUrl !== url);
      
      if (updateDeviceBrowser) {
        await updateDeviceBrowser({
          device: deviceId as any,
          allowedUrls: newWhitelistedUrls
        });
      }
      
      toast({
        title: "URL Removed",
        description: `${url} has been removed from the whitelist.`,
      });
    } catch (error) {
      console.error('Error removing URL from whitelist:', error);
      
      const errorMessage = "Failed to remove URL from whitelist. Please try again.";
      
      toast({
        title: "Error",
        description: (
          <div className="flex items-center justify-between gap-2">
            <span className="flex-1">{errorMessage}</span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => copyToClipboard(errorMessage)}
              className="h-6 w-6 p-0"
            >
              <Copy className="h-3 w-3" />
            </Button>
          </div>
        ),
        variant: "destructive",
      });
    }
  };

  const filteredSites = currentWhitelistedSites.filter(url =>
    url.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Loading state
  if (!deviceData || !deviceBrowserConfig) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium">Browser Whitelist</h3>
        <p className="text-gray-600">Websites explicitly allowed for this device</p>
      </div>

      {/* Add URL Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Add Website to Whitelist</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder="Enter website URL (e.g., example.com)"
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              className="flex-1"
            />
            <Button onClick={handleAddUrl}>
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Search Section */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <Input
          placeholder="Search whitelisted websites..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Current Whitelist */}
      <div>
        <h4 className="font-medium mb-3">Current Browser Whitelist</h4>
        {filteredSites.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-8">
              <Shield className="w-8 h-8 text-gray-400 mb-2" />
              <p className="text-sm text-gray-500">
                {searchQuery ? "No websites found matching your search" : "No websites in whitelist"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {filteredSites.map((url, index) => (
              <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-blue-500" />
                  <div>
                    <p className="font-medium text-sm">{url}</p>
                    <p className="text-xs text-gray-500">Website URL</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="default">Allowed</Badge>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => handleRemoveUrl(url)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};